import React from "react";
import { Icon, Ring, DashTodos, confirmDialog, clickable, RoiPanel } from "./registry";
import { PushPromptCard } from "./pushsettings";
import { VerticalChip } from "./registry";   // "Concrete" on a concrete lead; nothing on a fence lead
import { VERTICALS, inLens, measureOf } from "./lib/verticals";   // the lens: whose numbers the stat cards add up
/* ============================================================
   Fence Works — Dashboard
   ============================================================ */
const { money, money0 } = window.VFP;

function MiniCard({ kicker, children, right, className = "", style }: any) {
  return (
    <div className={"card pad " + className} style={style}>
      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="kicker">{kicker}</span>{right}
      </div>
      {children}
    </div>
  );
}

function StatCard({ icon, value, label, accent }: any) {
  return (
    <div className="card hover" style={{ padding: "18px 18px 16px" }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center",
        background: "var(--surface-2)", border: "1px solid var(--line-2)", color: accent || "var(--fg-2)", marginBottom: 14 }}>
        <Icon name={icon} size={19} />
      </div>
      <div className="display tnum" style={{ fontSize: 30 }}>{value}</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{label}</div>
    </div>
  );
}

/* ---- Sales Leaderboard: performance per rep, straight from store.quotes.
   A quote carries `rep` (stamped in the Estimator, defaulting to whoever sent
   it); grouping + ranking happens here — no new persisted state. Conventions
   match reports.tsx: won = signed/paid, pipeline = pending/viewed, collected =
   manual + Stripe payments (falling back to depositPaid). ---- */
const LB_WON = ["signed", "paid"];
const LB_OPEN = ["pending", "viewed"];
function lbCollected(q: any): number {
  const manual = (Array.isArray(q.payments) ? q.payments : []).reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
  const online = (Array.isArray(q.stripePayments) ? q.stripePayments : []).reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
  // depositPaid already holds the Stripe sum (webhook) — don't double-count
  // it against the itemized stripePayments ledger; take whichever is larger.
  return manual + Math.max(online, Number(q.depositPaid) || 0);
}
const LB_PERIODS = [["month", "This Month"], ["year", "This Year"], ["all", "All Time"]];
function SalesLeaderboard({ store, go }: any) {
  const quotes = store.quotes || [];
  const [period, setPeriod] = React.useState<any>("all");
  const now = new Date();
  const from = period === "month" ? new Date(now.getFullYear(), now.getMonth(), 1).getTime()
    : period === "year" ? new Date(now.getFullYear(), 0, 1).getTime() : 0;
  const rows = React.useMemo(() => {
    const by: any = {};
    quotes.forEach((q: any) => {
      if ((q.createdAt || 0) < from) return;
      const key = (q.rep || "").trim() || "Unassigned";
      const r = by[key] || (by[key] = { rep: key, sent: 0, won: 0, pipeline: 0, revenue: 0, collected: 0 });
      r.sent++;
      if (LB_WON.includes(q.status)) { r.won++; r.revenue += q.total || 0; }
      else if (LB_OPEN.includes(q.status)) r.pipeline += q.total || 0;
      r.collected += lbCollected(q);
    });
    return Object.values(by)
      .sort((a: any, b: any) => (b.revenue - a.revenue) || (b.pipeline - a.pipeline) || (b.sent - a.sent))
      .sort((a: any, b: any) => (a.rep === "Unassigned" ? 1 : 0) - (b.rep === "Unassigned" ? 1 : 0));   // stable: untagged sinks to the bottom
  }, [quotes, from]);
  if (!quotes.length) return null;
  const medal = ["#f5c451", "#c0c8d4", "#d19a6b"];   // gold / silver / bronze rank dots
  const closeCol = (pct: number) => pct >= 40 ? "var(--green)" : pct >= 25 ? "var(--amber)" : "var(--red)";
  const allUntagged = rows.length > 0 && rows.every((r: any) => r.rep === "Unassigned");
  return (
    <div className="card pad" style={{ marginTop: 18 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
        <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Sales Leaderboard <span className="chip" style={{ fontSize: 11, padding: "1px 8px" }}>{rows.length} rep{rows.length === 1 ? "" : "s"}</span></h3>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {LB_PERIODS.map(([id, lb]) => (
            <button key={id} className={"filter-chip" + (period === id ? " on" : "")} onClick={() => setPeriod(id)}>{lb}</button>
          ))}
          <button className="btn btn-outline btn-sm" onClick={() => go("salesteam")} title="Roster, commission % and monthly goals per rep"><Icon name="user" size={13} />Manage team</button>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Performance by rep, ranked by revenue won — quotes are credited to the rep set on the estimate.</p>
      {rows.length === 0
        ? <EmptyState icon="user" title="No quotes in this period" sub="Switch the period, or send a quote from the Estimator." />
        : <>
          <div style={{ overflowX: "auto" }}>
            <table className="rep-src-table">
              <thead><tr><th>#</th><th>Rep</th><th className="num">Sent</th><th className="num">Won</th><th className="num">Close rate</th><th className="num">Pipeline</th><th className="num">Revenue</th><th className="num">Collected</th></tr></thead>
              <tbody>
                {rows.map((r: any, i: number) => {
                  const close = r.sent ? Math.round((r.won / r.sent) * 100) : 0;
                  const untagged = r.rep === "Unassigned";
                  return (
                    <tr key={r.rep}>
                      <td><span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 99, fontSize: 11, fontWeight: 800, color: !untagged && i < 3 ? "#06121a" : "var(--fg-3)", background: !untagged && i < 3 ? medal[i] : "var(--surface-3)" }}>{i + 1}</span></td>
                      <td style={{ fontWeight: 700, color: untagged ? "var(--fg-3)" : "var(--fg)" }}>{r.rep}</td>
                      <td className="num tnum">{r.sent}</td>
                      <td className="num tnum">{r.won}</td>
                      <td className="num"><span className="tnum" style={{ fontWeight: 700, color: closeCol(close) }}>{close}%</span></td>
                      <td className="num tnum" style={{ color: "var(--fg-2)" }}>{r.pipeline > 0 ? money0(r.pipeline) : "—"}</td>
                      <td className="num tnum" style={{ fontWeight: 700, color: r.revenue > 0 ? "var(--teal)" : "var(--fg-3)" }}>{r.revenue > 0 ? money0(r.revenue) : "—"}</td>
                      <td className="num tnum" style={{ color: r.collected > 0 ? "var(--green)" : "var(--fg-3)" }}>{r.collected > 0 ? money0(r.collected) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {allUntagged && <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Tip: quotes aren't credited to anyone yet — set the <b style={{ color: "var(--fg-3)" }}>Sales rep</b> in the Estimator's Customer &amp; Job Info (it defaults to whoever's signed in on new estimates).</p>}
        </>}
    </div>
  );
}

/* ---- Lead Inbox: unclaimed website/booking leads with a waiting timer.
   Speed-to-lead is the #1 close-rate lever — the timer makes it visible, one
   tap claims it, and round-robin (toggle) hands new leads to the roster
   automatically as they arrive. ---- */
function LeadInbox({ store, go, auth }: any) {
  const s = store.settings || {};
  const me = (auth && auth.user && (auth.user.name || auth.user.email)) || "";
  const leads = (store.quotes || [])
    .filter((q: any) => (q.source === "widget" || q.source === "booking") && !q.rep
      && (q.status === "pending" || q.status === "viewed")
      && Date.now() - (q.createdAt || 0) < 14 * 86400000)
    .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
  if (!leads.length) return null;
  const waiting = (t: number) => { const m = Math.floor((Date.now() - t) / 60000); return m < 60 ? m + " min" : m < 1440 ? Math.floor(m / 60) + " hr" : Math.floor(m / 1440) + " d"; };
  const slaCol = (t: number) => { const m = (Date.now() - t) / 60000; return m < 15 ? "var(--green)" : m < 60 ? "var(--amber)" : "var(--red)"; };
  const claim = (q: any) => { store.updateQuote(q.id, { rep: me || undefined }); store.addActivity(`${me || "You"} claimed the ${q.customer || ""} lead`); };
  return (
    <div className="card pad" data-tour="lead-inbox" style={{ marginBottom: 18, borderColor: "var(--teal-tint-2)", boxShadow: "inset 0 0 60px -40px var(--teal-glow)" }}>
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Lead Inbox <span className="chip" style={{ fontSize: 11, padding: "1px 8px", color: "var(--teal)" }}>{leads.length} waiting</span></h3>
        <button className={"toggle-row" + (s.leadRoundRobin ? " on" : "")} style={{ padding: "5px 10px" }} onClick={() => store.setSettings({ leadRoundRobin: !s.leadRoundRobin })}
          title="Hand each new lead to the next rep on the Sales Team roster the moment it arrives">
          <span className={"sw" + (s.leadRoundRobin ? " on" : "")}><span className="knob" /></span>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Round-robin to reps</span>
        </button>
      </div>
      <div className="lead-list" style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}>
        {leads.map((q: any) => (
          <div key={q.id} className="proj-row lead-row" style={{ cursor: "default" }}>
            <span className="chip" style={{ fontSize: 10.5, textTransform: "capitalize", flexShrink: 0 }}>{q.source}</span>
            <VerticalChip record={q} />
            <div className="lead-info">
              <div style={{ fontWeight: 700, fontSize: 14.5 }}>{q.customer || "New lead"}</div>
              <div className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[q.address, q.phone, q.email].filter(Boolean).join(" · ") || "No contact captured"}</div>
            </div>
            <div className="lead-actions">
              {q.total > 0 && <div className="display tnum" style={{ fontSize: 15, color: "var(--teal)" }}>~{money0(q.total)}</div>}
              <span className="chip" style={{ fontSize: 11, color: slaCol(q.createdAt), borderColor: "var(--line-2)", flexShrink: 0 }} title="Waiting since the lead arrived — under 15 minutes wins the job">
                ⏱ {waiting(q.createdAt)}
              </span>
              <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => claim(q)} title="Take this lead — credits you as the rep">Claim</button>
              <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => go("quote", { quoteId: q.id })}>Open</button>
            </div>
          </div>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Answer in under 15 minutes and you win most of these. Claiming credits you on the leaderboard; enable alerts from the bell so new leads buzz your phone.</p>
    </div>
  );
}

/* ---- Reviews Pulse: is the review engine actually producing? Requests come
   from completed jobs (the follow-up panel); this shows conversion and who to
   nudge again. ---- */
function ReviewsPulse({ store, go }: any) {
  const wos = (store.workOrders || []).filter((w: any) => w.reviewRequested);
  if (!wos.length) return null;
  const received = wos.filter((w: any) => w.reviewReceived);
  const conv = Math.round((received.length / wos.length) * 100);
  const stale = wos.filter((w: any) => !w.reviewReceived && Date.now() - (w.reviewRequestedAt || 0) > 7 * 86400000).slice(0, 3);
  return (
    <MiniCard kicker="Reviews Pulse" right={<span className="tnum" style={{ fontSize: 12, fontWeight: 800, color: conv >= 40 ? "var(--green)" : "var(--amber)" }}>{conv}%</span>}>
      <div className="row between" style={{ fontSize: 12.5, marginBottom: 8 }}>
        <span className="muted">Requested</span><span className="tnum" style={{ fontWeight: 700 }}>{wos.length}</span>
      </div>
      <div className="row between" style={{ fontSize: 12.5, marginBottom: stale.length ? 10 : 0 }}>
        <span className="muted">Received ★</span><span className="tnum" style={{ fontWeight: 700, color: "var(--green)" }}>{received.length}</span>
      </div>
      {stale.length > 0 && (
        <div style={{ display: "grid", gap: 6 }}>
          <div className="kicker" style={{ fontSize: 9.5 }}>Nudge again · 7d+ quiet</div>
          {stale.map((w: any) => (
            <button key={w.id} className="quick-link" style={{ padding: "6px 8px" }} onClick={() => go("workorders", { openWorkOrder: w.id })}>
              <Icon name="send" size={13} /><span style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.customer || w.title}</span>
            </button>
          ))}
        </div>
      )}
    </MiniCard>
  );
}

function IntegrationRow({ name, status, onClick }: any) {
  const live = status === "connected";
  return (
    <div className="row between" style={{ padding: "9px 0", borderBottom: "1px solid var(--line)" }}>
      <span className="row gap-8" style={{ fontSize: 13.5, fontWeight: 600 }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: live ? "var(--green)" : "var(--fg-faint)" }} />
        {name}
      </span>
      {live
        ? <span className="mono" style={{ fontSize: 11, color: "var(--green)" }}>Connected</span>
        : <button className="btn btn-outline btn-sm" onClick={onClick}>Connect</button>}
    </div>
  );
}

/* ---- Get-set-up checklist ----------------------------------------------------
   Computed from real state (nothing to "complete" by hand), walks the money
   path in order, and disappears once every step is done — or when dismissed
   on this device. Replaces the old single "Step 1 of 1" card. */
const SETUP_KEY = "fw_setup_dismissed";
function setupSteps(store: any) {
  const s = store.settings || {};
  const integ = s.integrations || {};
  const quotes = store.quotes || [];
  return [
    { id: "company", label: "Add your company details", sub: "Name, logo and contact info go on every quote and the customer hub.", done: !!((store.company && store.company.name) || s.companyName), route: ["settings", { section: "company" }] },
    { id: "product", label: "Set up your first product", sub: "Pick a fence style, enter your price per foot — the bill of materials builds itself.", done: (store.customProducts || []).length > 0, route: ["products"] },
    { id: "estimate", label: "Draw your first estimate", sub: "Type an address, trace the fence on the satellite map, see the total.", done: (store.projects || []).length > 0, route: ["estimator"] },
    { id: "quote", label: "Send a quote to sign & pay", sub: "Text or email the link; the customer e-signs and pays a deposit from their phone.", done: quotes.some((q: any) => (Array.isArray(q.sends) ? q.sends.length : 0) >= 1 || (q.status && q.status !== "pending")), route: ["estimates"] },
    { id: "stripe", label: "Connect Stripe for deposits", sub: "Deposits land in your bank, not ours. Starter and up.", done: !!(integ.stripe && integ.stripe.connected), route: ["integrations"] },
    { id: "ai", label: "Turn on the AI call team", sub: "Every call answered and booked, 24/7. Pro and up.", done: !!(s.ai && s.ai.receptionist && (s.ai.receptionist.number || s.ai.receptionist.enabled || s.ai.receptionist.on)), route: ["ai"] },
  ];
}
/* The owner's morning question is "what's happening today and what's stuck?"
   One strip, three answers, each a tap away: jobs on site today, leads
   waiting on a reply, balances still owed. Hidden while every count is
   zero — an empty strip is noise. */
function TodayStrip({ store, go }: any) {
  const today = new Date(); const ymd = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
  const wos = (store.workOrders || []).filter((w: any) => {
    const s = String(w.scheduledStart || "").slice(0, 10), e = String(w.scheduledEnd || s).slice(0, 10);
    return s && s <= ymd && ymd <= e && w.status !== "completed" && w.status !== "cancelled";
  });
  const todos = (window as any).buildTodos ? (window as any).buildTodos(store) : [];
  const leads = todos.filter((t: any) => t.kind === "quote");
  const balances = todos.filter((t: any) => t.kind === "balance");
  const owed = balances.reduce((a: number, t: any) => a + (t.value || 0), 0);
  if (!wos.length && !leads.length && !balances.length) return null;
  const label = today.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const crews = Array.from(new Set(wos.map((w: any) => w.crewId).filter(Boolean))).length;
  return (
    <div className="today-strip" role="region" aria-label={"Today, " + label}>
      <span className="kicker today-kicker">Today · {label}</span>
      <button className={"today-tile" + (wos.length ? " on" : "")} onClick={() => go("calendar")}>
        <Icon name="calendar" size={15} /><b className="tnum">{wos.length}</b>
        <span>{wos.length === 1 ? "job on site" : "jobs on site"}{crews > 1 ? ` · ${crews} crews` : ""}</span>
      </button>
      <button className={"today-tile" + (leads.length ? " hot" : "")} onClick={() => go("todo")}>
        <Icon name="send" size={15} /><b className="tnum">{leads.length}</b>
        <span>{leads.length === 1 ? "lead waiting" : "leads waiting"}</span>
      </button>
      <button className={"today-tile" + (owed > 0 ? " warm" : "")} onClick={() => go("todo")}>
        <Icon name="dollar" size={15} /><b className="tnum">{money0(owed)}</b>
        <span>{balances.length === 1 ? "balance due" : "in balances due"}</span>
      </button>
    </div>
  );
}

function SetupChecklist({ store, go }: any) {
  const [hidden, setHidden] = React.useState<any>(() => { try { return localStorage.getItem(SETUP_KEY) === "1"; } catch (e) { return false; } });
  const [expanded, setExpanded] = React.useState<any>(false);
  const steps = setupSteps(store);
  const done = steps.filter(s => s.done).length;
  if (hidden || done === steps.length) return null;
  const next = steps.find(s => !s.done);
  const dismiss = () => { try { localStorage.setItem(SETUP_KEY, "1"); } catch (e) {} setHidden(true); };
  // the money path (company → product → estimate → quote) is the first four
  // steps. Once it's done the big card has done its job: fold to one line with
  // the next step, so the day's work leads the dashboard.
  const moneyPathDone = steps.slice(0, 4).every(s => s.done);
  if (moneyPathDone && !expanded && next) return (
    <div className="card setup-compact">
      <span className="kicker kicker-teal" style={{ whiteSpace: "nowrap" }}>Set up · {done} of {steps.length}</span>
      <span className="setup-compact-next">Next: <b>{next.label}</b> <span className="muted">— {next.sub}</span></span>
      <button className="btn btn-primary btn-sm" onClick={() => go(next.route[0], next.route[1] || null)}>Start<Icon name="chevright" size={13} /></button>
      <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(true)}>All steps</button>
      <button className="btn btn-ghost btn-sm" onClick={dismiss} aria-label="Hide setup" title="Hide this checklist"><Icon name="x" size={13} /></button>
    </div>
  );
  return (
    <div className="card glow" style={{ padding: 22, marginBottom: 18, position: "relative", overflow: "hidden", borderColor: "var(--teal-tint-2)", boxShadow: "0 0 0 1px var(--teal-tint), inset 0 0 60px -30px var(--teal-glow)" }}>
      <div className="row between" style={{ alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <span className="kicker kicker-teal">Get set up · {done} of {steps.length} done</span>
          <h2 className="display" style={{ fontSize: 22, margin: "8px 0 4px" }}>{done === 0 ? "Quote your first fence in the next five minutes." : next ? `Next up: ${next.label.charAt(0).toLowerCase() + next.label.slice(1)}.` : "You're set up."}</h2>
          <p className="muted" style={{ fontSize: 13.5, margin: 0, maxWidth: 560 }}>Each step unlocks the next part of the money path — from a priced estimate to a signed quote to a deposit in the bank.</p>
        </div>
        <div className="row gap-8">
          {moneyPathDone && <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(false)}>Collapse</button>}
          <button className="btn btn-ghost btn-sm" onClick={dismiss} title="Hide this checklist">Hide</button>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: "var(--surface-3)", overflow: "hidden", margin: "14px 0 12px" }} aria-hidden="true">
        <div style={{ width: Math.round((done / steps.length) * 100) + "%", height: "100%", background: "var(--teal)", transition: "width .4s" }} />
      </div>
      <div className="setup-steps">
        {steps.map((st, i) => (
          <button key={st.id} className={"setup-step" + (st.done ? " done" : "") + (next && next.id === st.id ? " next" : "")} onClick={() => go(st.route[0], st.route[1] || null)} aria-label={`${st.label}${st.done ? " (done)" : ""}`}>
            <span className="ss-n">{st.done ? <Icon name="check" size={13} /> : i + 1}</span>
            <span style={{ minWidth: 0 }}><span className="ss-l">{st.label}</span><span className="ss-s">{st.sub}</span></span>
            {!st.done && <Icon name="chevright" size={14} className="muted" style={{ marginLeft: "auto", flexShrink: 0, alignSelf: "center" }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ store, go, auth }: any) {
  // the service lens (lib/verticals.ts): a no-op filter for every fence account
  const lens = store.lens || "fence";
  const V = VERTICALS[lens] || VERTICALS.fence;
  const projects = (store.projects || []).filter((p: any) => inLens(p, lens));
  const quotes = (store.quotes || []).filter((q: any) => inLens(q, lens));
  // greet with the signed-in user's real name + their company — never a canned
  // identity. With nothing to show, the copy stays generic.
  const firstName = (((auth && auth.user && auth.user.name) || "").trim().split(/\s+/)[0]) || "";
  const coName = (store.company && store.company.name) || (store.settings && store.settings.companyName) || "";
  const totalLF = projects.reduce((a, p) => a + measureOf(p).qty, 0);
  const revenue = quotes.filter(q => q.status === "paid" || q.status === "signed").reduce((a, q) => a + q.total, 0) ||
                  quotes.reduce((a, q) => a + q.total, 0);
  const health = Math.min(100, (store.companyReady ? 30 : 0) + Math.min(projects.length * 12, 36) + Math.min(quotes.length * 10, 34));
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening"; })();

  const [quoteFilter, setQuoteFilter] = React.useState<any>("All");
  const filtered = quoteFilter === "All" ? quotes : quotes.filter(q => q.status === quoteFilter.toLowerCase());

  return (
    <div className="dash page fade-up">
      <div className="dash-grid">
        {/* ---- left rail ---- */}
        <aside className="dash-left">
          <MiniCard kicker="Business Pulse" right={<span className="chip" style={{ padding: "2px 8px", fontSize: 11 }}>30d</span>}>
            <div style={{ display: "grid", placeItems: "center", padding: "6px 0 4px" }}>
              <Ring value={health} size={104} stroke={9}>
                <div style={{ textAlign: "center" }}>
                  <div className="display" style={{ fontSize: 26, color: health > 0 ? "var(--teal)" : "var(--fg-faint)" }}>{health || "—"}</div>
                  <div className="kicker" style={{ fontSize: 9 }}>score</div>
                </div>
              </Ring>
            </div>
            <p className="muted" style={{ fontSize: 12.5, textAlign: "center", margin: "8px 0 0" }}>Overall health score</p>
          </MiniCard>

          <MiniCard kicker="Quote Pipeline">
            {quotes.length === 0
              ? <><p className="muted" style={{ fontSize: 13, margin: "0 0 14px" }}>Send quotes to see your pipeline.</p>
                  <button className="btn btn-primary btn-block btn-sm" onClick={() => go("estimator")}><Icon name="plus" size={15} />Create an Estimate</button></>
              : <div style={{ display: "grid", gap: 9 }}>
                  {["pending", "viewed", "signed", "paid"].map(st => {
                    const n = quotes.filter(q => q.status === st).length;
                    const pct = Math.round((n / quotes.length) * 100);
                    return (
                      <div key={st}>
                        <div className="row between" style={{ fontSize: 12.5, marginBottom: 4 }}>
                          <span style={{ textTransform: "capitalize", color: "var(--fg-2)" }}>{st}</span><span className="tnum muted">{n}</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 99, background: "var(--surface-3)", overflow: "hidden" }}>
                          <div style={{ width: pct + "%", height: "100%", background: st === "paid" ? "var(--green)" : "var(--teal)" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>}
          </MiniCard>

          <MiniCard kicker="Quick Links">
            <div style={{ display: "grid", gap: 4 }}>
              {[["map", "New Estimate", "estimator"], ["box", "Products", "products"], ["gear", "Settings", "settings"]].map(([ic, lb, rt]) => (
                <button key={lb} className="quick-link" onClick={() => go(rt)}>
                  <Icon name={ic} size={17} /><span>{lb}</span><Icon name="chevright" size={14} className="muted" style={{ marginLeft: "auto" }} />
                </button>
              ))}
            </div>
          </MiniCard>
        </aside>

        {/* ---- main ---- */}
        <main className="dash-main">
          <div className="row between" style={{ marginBottom: 20, alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h1 className="display" style={{ fontSize: 30, margin: 0 }}>Good {greeting}{firstName ? `, ${firstName}` : ""}</h1>
              <p className="muted" style={{ margin: "5px 0 0", fontSize: 15 }}>Here's what's happening{coName ? ` at ${coName}` : " today"}</p>
            </div>
            {/* the shop-wall view of this same data — an action, not a place in the menu */}
            <button className="btn btn-ghost btn-sm dash-tv" onClick={() => go("tv")} title="Today's installs, the sales leaderboard and the pipeline, big-type and auto-rotating for the shop TV"><Icon name="activity" size={14} />Launch TV display</button>
          </div>

          {/* get-set-up checklist (hides itself once every step is done) */}
          <TodayStrip store={store} go={go} />
          <SetupChecklist store={store} go={go} />
          <RoiPanel store={store} go={go} />
          {/* after the first lead or signed quote: "Get a buzz when leads come in" (pushsettings.tsx) */}
          <PushPromptCard store={store} go={go} />
          {projects.length === 0 && (
            <div className="card glow" style={{ padding: 24, marginBottom: 18, position: "relative", overflow: "hidden",
              borderColor: "var(--teal-tint-2)", boxShadow: "0 0 0 1px var(--teal-tint), inset 0 0 60px -30px var(--teal-glow)" }}>
              <span className="kicker kicker-teal">First estimate</span>
              <h2 className="display" style={{ fontSize: 23, margin: "8px 0 8px" }}>Draw your first estimate</h2>
              <p className="muted" style={{ fontSize: 14.5, maxWidth: 640, margin: "0 0 18px" }}>
                Open the Estimator, type a customer's address, click the fence line on the satellite map, and pick a product.
                The app auto-calculates linear feet, materials, labor, and the quote total — then you send it to sign &amp; pay online.
              </p>
              <div className="row gap-12 dash-cta">
                <button className="btn btn-primary" onClick={() => go("estimator")}>Open the Estimator <Icon name="arrow" size={16} /></button>
                <button className="btn btn-ghost" onClick={() => go("products")}>Set up products</button>
              </div>
            </div>
          )}

          {/* stat cards */}
          <div className="stat-row">
            <StatCard icon="clipboard" value={projects.length} label="Total Projects" accent="var(--teal)" />
            <StatCard icon="ruler" value={totalLF ? money0(totalLF).replace("$", "") : 0} label={lens === "fence" ? "Total Fence Feet" : "Total Square Feet"} accent="var(--cyan)" />
            <StatCard icon="dollar" value={money0(revenue)} label="Estimated Revenue" accent="var(--green)" />
            <StatCard icon="box" value={store.productsCreated} label="Products Created" accent="var(--violet)" />
          </div>

          {/* unclaimed website/booking leads — claim before they go cold */}
          <LeadInbox store={store} go={go} auth={auth} />

          {/* sales leaderboard — who's winning (per-rep sent / won / close / revenue) */}
          <SalesLeaderboard store={store} go={go} />

          {/* open projects */}
          <div className="card pad" style={{ marginTop: 18 }}>
            <div className="row between" style={{ marginBottom: 14 }}>
              <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Open Projects <span className="chip" style={{ fontSize: 11, padding: "1px 8px" }}>{projects.length}</span></h3>
              <button className="btn btn-outline btn-sm" onClick={() => go("estimator")}><Icon name="plus" size={14} />New Estimate</button>
            </div>
            {projects.length === 0
              ? <EmptyState icon="clipboard" title="No projects yet" sub="Estimates you save in the Estimator land here." />
              : <div style={{ display: "grid", gap: 10 }}>
                  {projects.slice().reverse().map(p => {
                    // the map step is done once a quote exists — open the estimate;
                    // the map icon (or a quote-less project) still opens the estimator
                    const pq = (store.quotes || []).find(x => x.projectId === p.id);
                    return (
                    /* One grid, two templates: side-by-side on a desktop, and on a
                       phone the name gets its own full-width line instead of being
                       crushed between the job number and the price. */
                    <div key={p.id} className="proj-row proj-grid" {...clickable(() => pq ? go("quote", { quoteId: pq.id }) : go("estimator", { loadProject: p.id }), { label: `Open ${p.job || "this project"}` })}>
                      <div className="proj-id mono">{p.job}</div>
                      <div className="proj-name">{p.customer || "Untitled customer"}</div>
                      <div className="proj-sub muted">{p.address}</div>
                      <div className="proj-amt display tnum">{money0(p.total)}</div>
                      <div className="proj-meta muted">{Math.round(p.lf)} ft · {p.productName}</div>
                      <div className="proj-acts">
                        {pq && <button className="proj-del" title="Open the map in the Estimator" onClick={(e) => { e.stopPropagation(); go("estimator", { loadProject: p.id }); }}><Icon name="map" size={15} /></button>}
                        <button className="proj-del" title="Delete project" onClick={async (e) => { e.stopPropagation(); if (await confirmDialog({ title: `Delete ${p.job} (${p.customer || "untitled"})?`, body: "This can't be undone.", confirmLabel: "Delete project", danger: true })) store.deleteProject(p.id); }}><Icon name="trash" size={15} /></button>
                        <Icon name="chevright" size={16} className="muted" />
                      </div>
                    </div>
                  );})}
                </div>}
          </div>

          {/* sent quotes */}
          <div className="card pad" style={{ marginTop: 18 }}>
            <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Sent Quotes <span className="chip" style={{ fontSize: 11, padding: "1px 8px" }}>{quotes.length}</span></h3>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                {["All", "Pending", "Viewed", "Signed", "Paid"].map(f => (
                  <button key={f} className={"filter-chip" + (quoteFilter === f ? " on" : "")} onClick={() => setQuoteFilter(f)}>{f}</button>
                ))}
              </div>
            </div>
            {filtered.length === 0
              ? <EmptyState icon="send" title="No quotes yet" sub="Share a quote from the Estimator to track it here." />
              : <div style={{ display: "grid", gap: 10 }}>
                  {filtered.slice().reverse().map(q => <QuoteRow key={q.id} q={q} go={go} store={store} />)}
                </div>}
          </div>
        </main>

        {/* ---- right rail ---- */}
        <aside className="dash-right">
          <MiniCard kicker="Activity" right={<span style={{ width: 7, height: 7, borderRadius: 99, background: "var(--teal)" }} />}>
            {store.activity.length === 0
              ? <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Quotes you send from the Estimator will show activity here.</p>
              : <div style={{ display: "grid", gap: 12 }}>
                  {store.activity.slice(0, 6).map((a, k) => (
                    <div key={k} className="row gap-8" style={{ alignItems: "flex-start" }}>
                      <span style={{ marginTop: 5, width: 6, height: 6, borderRadius: 99, background: "var(--teal)", flexShrink: 0 }} />
                      <div><div style={{ fontSize: 13 }}>{a.text}</div><div className="faint" style={{ fontSize: 11.5 }}>{a.time}</div></div>
                    </div>
                  ))}
                </div>}
          </MiniCard>

          <MiniCard kicker="Needs Attention">
            <DashTodos store={store} go={go} />
          </MiniCard>

          <ReviewsPulse store={store} go={go} />

          <MiniCard kicker="Integrations" right={<button className="mono" style={{ fontSize: 11, color: "var(--teal)", background: "none", border: 0, cursor: "pointer" }} onClick={() => go("integrations")}>{(window.INTEGRATIONS || []).filter(i => (store.settings && store.settings.integrations && store.settings.integrations[i.id] || {}).connected).length}/{(window.INTEGRATIONS || []).length} ›</button>}>
            <div>
              {(window.INTEGRATIONS || []).map(it => {
                const conn = (store.settings && store.settings.integrations && store.settings.integrations[it.id]) || {};
                return <IntegrationRow key={it.id} name={it.name} status={conn.connected ? "connected" : ""} onClick={() => go("integrations")} />;
              })}
            </div>
          </MiniCard>
        </aside>
      </div>
    </div>
  );
}

function QuoteRow({ q, go, store }: any) {
  const colors = { pending: "var(--fg-3)", viewed: "var(--blue)", signed: "var(--violet)", paid: "var(--green)" };
  const [showHist, setShowHist] = React.useState<any>(false);
  const sends = Array.isArray(q.sends) ? q.sends : [];   // EST-02: never invented from createdAt
  const sentCount = sends.length;
  const fmt = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  async function del(e) {
    e.stopPropagation();
    if (await confirmDialog({ title: `Delete the quote for ${q.customer || "this customer"} (${money0(q.total)})?`, body: "This can't be undone.", confirmLabel: "Delete quote", danger: true })) store.deleteQuote(q.id);
  }
  return (
    <div className="quote-row-wrap">
      <div className="proj-row quote-row" {...clickable(() => go("quote", { quoteId: q.id }), { label: `Open the quote for ${q.customer || "this customer"}` })}>
        <div className="quote-row-main">
          <div className="quote-row-name">{q.customer || "Customer"}</div>
          <div className="muted quote-row-meta">{q.productName} · {Math.round(q.lf)} ft</div>
        </div>
        <div className="quote-row-trail">
          {sentCount > 1 && (
            <button className="quote-sends" onClick={(e) => { e.stopPropagation(); setShowHist(v => !v); }} title="Send history">
              <Icon name="send" size={12} />{sentCount}×<Icon name={showHist ? "chevdown" : "chevright"} size={12} />
            </button>
          )}
          <span className="chip" style={{ textTransform: "capitalize", color: colors[q.status], borderColor: "var(--line-2)" }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: colors[q.status] }} />{q.status}
          </span>
          <div className="display tnum quote-row-total">{money0(q.total)}</div>
          <button className="proj-del" title="Delete quote" onClick={del}><Icon name="trash" size={15} /></button>
          <Icon name="chevright" size={16} className="muted quote-row-chev" />
        </div>
      </div>
      {showHist && sentCount > 1 && (
        <div className="quote-hist">
          <div className="kicker" style={{ marginBottom: 6 }}>Sent {sentCount} times</div>
          {sends.slice().reverse().map((sd, i) => (
            <div key={i} className="row gap-8" style={{ fontSize: 12.5, padding: "3px 0" }}>
              <Icon name="send" size={12} className="muted" />
              <span style={{ color: "var(--fg-2)" }}>{fmt(sd.at)}</span>
              {i === 0 && <span className="chip" style={{ fontSize: 10, padding: "1px 7px" }}>latest</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Empty state with an optional way forward: `action` renders a primary
   button, `secondary` a ghost one — so an empty list is never a dead end. */
function EmptyState({ icon, title, sub, action, secondary }: any) {
  return (
    <div style={{ textAlign: "center", padding: "34px 16px", color: "var(--fg-3)" }}>
      <Icon name={icon} size={26} style={{ opacity: .5 }} />
      <div style={{ fontWeight: 700, marginTop: 10, color: "var(--fg-2)" }}>{title}</div>
      {sub && <div style={{ fontSize: 13, maxWidth: 440, margin: "3px auto 0" }}>{sub}</div>}
      {(action || secondary) && (
        <div className="row gap-8" style={{ justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
          {action && <button className="btn btn-primary btn-sm" onClick={action.onClick}>{action.icon && <Icon name={action.icon} size={14} />}{action.label}</button>}
          {secondary && <button className="btn btn-ghost btn-sm" onClick={secondary.onClick}>{secondary.label}</button>}
        </div>
      )}
    </div>
  );
}

Object.assign(window, { Dashboard, MiniCard, StatCard, EmptyState, IntegrationRow, QuoteRow, SalesLeaderboard, SetupChecklist, setupSteps });

export { MiniCard, StatCard, IntegrationRow, Dashboard, QuoteRow, EmptyState, SalesLeaderboard, SetupChecklist, setupSteps };
